/**
 * Vehicle Detail Page
 * ==================
 * صفحة تفاصيل المركبة الشاملة
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTenantAuth } from '../../context/TenantAuthContext';
import { useToast } from '../../context/ToastContext';
import SAR from '../../../components/SAR';
import { useDialog } from '../../../context/DialogContext';

const I = {
  back: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>,
  truck: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 16H9m10 0h3v-3.15a1 1 0 0 0-.84-.99L16 11l-2.7-3.6a1 1 0 0 0-.8-.4H5.24a2 2 0 0 0-1.8 1.1l-.8 1.63A6 6 0 0 0 2 12.42V16h2"/><circle cx="6.5" cy="16.5" r="2.5"/><circle cx="16.5" cy="16.5" r="2.5"/></svg>,
  wrench: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>,
  file: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>,
  fuel: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18H3z"/><path d="M15 10h4a2 2 0 0 1 2 2v8a2 2 0 0 0 2 2"/><path d="M7 6h4"/></svg>,
  user: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>,
  alert: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>,
  chart: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>,
  plus: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>,
  x: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
  calendar: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
  trash: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>,
};

const VehicleDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { apiCall } = useTenantAuth();
  const { success, error: showError } = useToast();
  const { confirm } = useDialog();

  const [loading, setLoading] = useState(true);
  const [vehicle, setVehicle] = useState(null);
  const [activeTab, setActiveTab] = useState('info');
  
  // Data states
  const [maintenance, setMaintenance] = useState({ items: [], total_records: 0, total_cost: 0 });
  const [documents, setDocuments] = useState([]);
  const [fuel, setFuel] = useState({ items: [], total_records: 0, total_liters: 0, total_cost: 0 });
  const [drivers, setDrivers] = useState({ current: [], history: [] });
  const [incidents, setIncidents] = useState([]);
  const [stats, setStats] = useState(null);
  const [availableDrivers, setAvailableDrivers] = useState([]);

  // Modal states
  const [showMaintenanceModal, setShowMaintenanceModal] = useState(false);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [showFuelModal, setShowFuelModal] = useState(false);
  const [showDriverModal, setShowDriverModal] = useState(false);
  const [showIncidentModal, setShowIncidentModal] = useState(false);

  // Form states
  const [maintenanceForm, setMaintenanceForm] = useState({
    maintenance_type: '', description: '', cost: '', odometer_reading: '',
    performed_by: '', performed_at: '', next_maintenance_date: '', parts_replaced: ''
  });
  const [documentForm, setDocumentForm] = useState({
    document_type: '', document_number: '', issue_date: '', expiry_date: '', notes: ''
  });
  const [documentFile, setDocumentFile] = useState(null);
  const [fuelForm, setFuelForm] = useState({
    liters: '', cost: '', odometer_reading: '', fuel_type: '', station: '', filled_at: ''
  });
  const [driverForm, setDriverForm] = useState({ driver_id: '', start_date: '', is_primary: true });
  const [incidentForm, setIncidentForm] = useState({
    incident_type: '', description: '', incident_date: '', location: '', damage_cost: '', status: 'reported'
  });

  const loadVehicle = useCallback(async () => {
    try {
      const data = await apiCall(`/tenant/vehicles/${id}`);
      setVehicle(data);
    } catch (err) {
      showError('فشل تحميل بيانات المركبة');
      navigate('/tenant/vehicles');
    } finally {
      setLoading(false);
    }
  }, [apiCall, id, navigate, showError]);

  const loadMaintenance = useCallback(async () => {
    try {
      const data = await apiCall(`/tenant/vehicles/${id}/maintenance`);
      setMaintenance(data);
    } catch (err) { console.error(err); }
  }, [apiCall, id]);

  const loadDocuments = useCallback(async () => {
    try {
      const data = await apiCall(`/tenant/vehicles/${id}/documents`);
      setDocuments(data);
    } catch (err) { console.error(err); }
  }, [apiCall, id]);

  const loadFuel = useCallback(async () => {
    try {
      const data = await apiCall(`/tenant/vehicles/${id}/fuel`);
      setFuel(data);
    } catch (err) { console.error(err); }
  }, [apiCall, id]);

  const loadDrivers = useCallback(async () => {
    try {
      const data = await apiCall(`/tenant/vehicles/${id}/drivers`);
      setDrivers(data);
    } catch (err) { console.error(err); }
  }, [apiCall, id]);

  const loadIncidents = useCallback(async () => {
    try {
      const data = await apiCall(`/tenant/vehicles/${id}/incidents`);
      setIncidents(data);
    } catch (err) { console.error(err); }
  }, [apiCall, id]);

  const loadStats = useCallback(async () => {
    try {
      const data = await apiCall(`/tenant/vehicles/${id}/stats`);
      setStats(data);
    } catch (err) { console.error(err); }
  }, [apiCall, id]);

  const loadAvailableDrivers = useCallback(async () => {
    try {
      const data = await apiCall('/tenant/drivers?status=active&limit=100');
      setAvailableDrivers(data.items || []);
    } catch (err) { console.error(err); }
  }, [apiCall]);

  useEffect(() => { loadVehicle(); }, [loadVehicle]);

  useEffect(() => {
    if (!vehicle) return;
    switch (activeTab) {
      case 'maintenance': loadMaintenance(); break;
      case 'documents': loadDocuments(); break;
      case 'fuel': loadFuel(); break;
      case 'drivers': loadDrivers(); loadAvailableDrivers(); break;
      case 'incidents': loadIncidents(); break;
      case 'stats': loadStats(); break;
      default: break;
    }
  }, [activeTab, vehicle, loadMaintenance, loadDocuments, loadFuel, loadDrivers, loadIncidents, loadStats, loadAvailableDrivers]);

  const handleAddMaintenance = async (e) => {
    e.preventDefault();
    try {
      await apiCall(`/tenant/vehicles/${id}/maintenance`, {
        method: 'POST',
        body: JSON.stringify({
          ...maintenanceForm,
          cost: maintenanceForm.cost ? parseFloat(maintenanceForm.cost) : null,
          odometer_reading: maintenanceForm.odometer_reading ? parseInt(maintenanceForm.odometer_reading) : null
        })
      });
      success('تم تسجيل الصيانة بنجاح');
      setShowMaintenanceModal(false);
      setMaintenanceForm({ maintenance_type: '', description: '', cost: '', odometer_reading: '', performed_by: '', performed_at: '', next_maintenance_date: '', parts_replaced: '' });
      loadMaintenance();
      loadVehicle();
    } catch (err) { showError(err.message || 'فشل تسجيل الصيانة'); }
  };

  const handleAddDocument = async (e) => {
    e.preventDefault();
    try {
      const result = await apiCall(`/tenant/vehicles/${id}/documents`, { method: 'POST', body: JSON.stringify(documentForm) });
      
      // Upload file if selected
      if (documentFile && result?.id) {
        const token = localStorage.getItem('watrly_tenant_token');
        const uploadForm = new FormData();
        uploadForm.append('file', documentFile);
        uploadForm.append('vehicle_id', id);
        uploadForm.append('document_type', documentForm.document_type);
        await fetch('http://localhost:8002/api/v1/tenant/uploads/vehicle-document', {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: uploadForm
        });
      }
      
      success('تم إضافة الوثيقة بنجاح');
      setShowDocumentModal(false);
      setDocumentForm({ document_type: '', document_number: '', issue_date: '', expiry_date: '', notes: '' });
      setDocumentFile(null);
      loadDocuments();
      loadVehicle();
    } catch (err) { showError(err.message || 'فشل إضافة الوثيقة'); }
  };

  const handleAddFuel = async (e) => {
    e.preventDefault();
    try {
      await apiCall(`/tenant/vehicles/${id}/fuel`, {
        method: 'POST',
        body: JSON.stringify({
          ...fuelForm,
          liters: parseFloat(fuelForm.liters),
          cost: parseFloat(fuelForm.cost),
          odometer_reading: fuelForm.odometer_reading ? parseInt(fuelForm.odometer_reading) : null
        })
      });
      success('تم تسجيل التعبئة بنجاح');
      setShowFuelModal(false);
      setFuelForm({ liters: '', cost: '', odometer_reading: '', fuel_type: '', station: '', filled_at: '' });
      loadFuel();
      loadVehicle();
    } catch (err) { showError(err.message || 'فشل تسجيل التعبئة'); }
  };

  const handleAssignDriver = async (e) => {
    e.preventDefault();
    try {
      await apiCall(`/tenant/vehicles/${id}/drivers`, { method: 'POST', body: JSON.stringify(driverForm) });
      success('تم تعيين السائق بنجاح');
      setShowDriverModal(false);
      setDriverForm({ driver_id: '', start_date: '', is_primary: true });
      loadDrivers();
    } catch (err) { showError(err.message || 'فشل تعيين السائق'); }
  };

  const handleUnassignDriver = async (assignmentId) => {
    if (!await confirm({ title: 'إلغاء التعيين', message: 'هل تريد إلغاء تعيين هذا السائق؟', type: 'warning', confirmText: 'إلغاء التعيين', cancelText: 'تراجع' })) return;
    try {
      await apiCall(`/tenant/vehicles/${id}/drivers/${assignmentId}`, { method: 'DELETE' });
      success('تم إلغاء التعيين');
      loadDrivers();
    } catch (err) { showError('فشل إلغاء التعيين'); }
  };

  const handleAddIncident = async (e) => {
    e.preventDefault();
    try {
      await apiCall(`/tenant/vehicles/${id}/incidents`, {
        method: 'POST',
        body: JSON.stringify({
          ...incidentForm,
          damage_cost: incidentForm.damage_cost ? parseFloat(incidentForm.damage_cost) : null
        })
      });
      success('تم تسجيل الحادث بنجاح');
      setShowIncidentModal(false);
      setIncidentForm({ incident_type: '', description: '', incident_date: '', location: '', damage_cost: '', status: 'reported' });
      loadIncidents();
    } catch (err) { showError(err.message || 'فشل تسجيل الحادث'); }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('ar-SA') : '-';
  const formatCurrency = (n) => n ? `${n.toLocaleString('ar-SA')}` : '-';

  const tabs = [
    { id: 'info', label: 'المعلومات', icon: I.truck },
    { id: 'maintenance', label: 'الصيانة', icon: I.wrench },
    { id: 'documents', label: 'الوثائق', icon: I.file },
    { id: 'fuel', label: 'الوقود', icon: I.fuel },
    { id: 'drivers', label: 'السائقين', icon: I.user },
    { id: 'incidents', label: 'الحوادث', icon: I.alert },
    { id: 'stats', label: 'الإحصائيات', icon: I.chart },
  ];

  const maintenanceTypes = [
    { value: 'routine', label: 'صيانة دورية' },
    { value: 'oil_change', label: 'تغيير زيت' },
    { value: 'tire_change', label: 'تغيير إطارات' },
    { value: 'brake_service', label: 'صيانة فرامل' },
    { value: 'repair', label: 'إصلاح' },
    { value: 'inspection', label: 'فحص' },
    { value: 'other', label: 'أخرى' },
  ];

  const documentTypes = [
    { value: 'registration', label: 'رخصة السير' },
    { value: 'insurance', label: 'التأمين' },
    { value: 'inspection', label: 'الفحص الدوري' },
    { value: 'permit', label: 'تصريح' },
    { value: 'other', label: 'أخرى' },
  ];

  const incidentTypes = [
    { value: 'accident', label: 'حادث' },
    { value: 'breakdown', label: 'عطل' },
    { value: 'theft', label: 'سرقة' },
    { value: 'damage', label: 'تلف' },
    { value: 'other', label: 'أخرى' },
  ];

  if (loading) {
    return <div className="page-loading"><div className="spinner" /></div>;
  }

  if (!vehicle) {
    return <div className="page-empty"><h3>المركبة غير موجودة</h3></div>;
  }

  return (
    <div className="vehicle-detail-page">
      {/* Header */}
      <div className="detail-header">
        <button className="back-btn" onClick={() => navigate('/tenant/vehicles')}>{I.back}</button>
        <div className="detail-header__info">
          <div className="detail-avatar" style={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)' }}>{I.truck}</div>
          <div className="detail-header__text">
            <h1>{vehicle.make} {vehicle.model} {vehicle.year}</h1>
            <p dir="ltr">{vehicle.plate_number}</p>
          </div>
        </div>
        <span className={`badge badge--${vehicle.status === 'active' ? 'success' : vehicle.status === 'maintenance' ? 'warning' : 'danger'}`}>
          {vehicle.status === 'active' ? 'نشط' : vehicle.status === 'maintenance' ? 'صيانة' : 'غير نشط'}
        </span>
      </div>

      {/* Tabs */}
      <div className="detail-tabs">
        {tabs.map(tab => (
          <button key={tab.id} className={`detail-tab ${activeTab === tab.id ? 'active' : ''}`} onClick={() => setActiveTab(tab.id)}>
            {tab.icon} <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="detail-content">
        {/* Info Tab */}
        {activeTab === 'info' && (
          <div className="info-grid">
            <div className="info-card">
              <h3>معلومات المركبة</h3>
              <div className="info-row"><span>الشركة المصنعة</span><strong>{vehicle.make || '-'}</strong></div>
              <div className="info-row"><span>الموديل</span><strong>{vehicle.model || '-'}</strong></div>
              <div className="info-row"><span>سنة الصنع</span><strong>{vehicle.year || '-'}</strong></div>
              <div className="info-row"><span>اللون</span><strong>{vehicle.color || '-'}</strong></div>
              <div className="info-row"><span>رقم اللوحة</span><strong dir="ltr">{vehicle.plate_number}</strong></div>
              <div className="info-row"><span>نوع الوقود</span><strong>{vehicle.fuel_type === 'diesel' ? 'ديزل' : vehicle.fuel_type === 'petrol' ? 'بنزين' : vehicle.fuel_type || '-'}</strong></div>
            </div>
            <div className="info-card">
              <h3>السعة والعداد</h3>
              <div className="info-row"><span>السعة (طن)</span><strong>{vehicle.capacity_tons || '-'}</strong></div>
              <div className="info-row"><span>السعة (لتر)</span><strong>{vehicle.capacity_liters || '-'}</strong></div>
              <div className="info-row"><span>قراءة العداد</span><strong>{vehicle.odometer_reading ? `${vehicle.odometer_reading.toLocaleString()} كم` : '-'}</strong></div>
            </div>
            <div className="info-card">
              <h3>تواريخ الانتهاء</h3>
              <div className="info-row">
                <span>رخصة السير</span>
                <strong className={vehicle.registration_expiry && new Date(vehicle.registration_expiry) < new Date() ? 'text-danger' : ''}>
                  {formatDate(vehicle.registration_expiry)}
                </strong>
              </div>
              <div className="info-row">
                <span>التأمين</span>
                <strong className={vehicle.insurance_expiry && new Date(vehicle.insurance_expiry) < new Date() ? 'text-danger' : ''}>
                  {formatDate(vehicle.insurance_expiry)}
                </strong>
              </div>
              <div className="info-row">
                <span>الفحص الدوري</span>
                <strong className={vehicle.inspection_expiry && new Date(vehicle.inspection_expiry) < new Date() ? 'text-danger' : ''}>
                  {formatDate(vehicle.inspection_expiry)}
                </strong>
              </div>
            </div>
            <div className="info-card">
              <h3>الصيانة</h3>
              <div className="info-row"><span>آخر صيانة</span><strong>{formatDate(vehicle.last_maintenance_date)}</strong></div>
              <div className="info-row"><span>الصيانة القادمة</span><strong>{formatDate(vehicle.next_maintenance_date)}</strong></div>
            </div>
          </div>
        )}

        {/* Maintenance Tab */}
        {activeTab === 'maintenance' && (
          <div className="tab-section">
            <div className="section-header">
              <div>
                <h3>سجل الصيانة</h3>
                <p>{maintenance.total_records} سجل • إجمالي التكلفة: {formatCurrency(maintenance.total_cost)}</p>
              </div>
              <button className="btn btn--primary btn--sm" onClick={() => setShowMaintenanceModal(true)}>{I.plus} إضافة صيانة</button>
            </div>
            {maintenance.items.length === 0 ? (
              <div className="empty-state">{I.wrench}<p>لا يوجد سجلات صيانة</p></div>
            ) : (
              <div className="records-list">
                {maintenance.items.map(m => (
                  <div key={m.id} className="record-card">
                    <div className="record-header">
                      <span className="record-type">{maintenanceTypes.find(t => t.value === m.maintenance_type)?.label || m.maintenance_type}</span>
                      <span className="record-date">{formatDate(m.performed_at)}</span>
                    </div>
                    {m.description && <p className="record-desc">{m.description}</p>}
                    <div className="record-meta">
                      {m.cost && <span>التكلفة: {formatCurrency(m.cost)}</span>}
                      {m.odometer_reading && <span>العداد: {m.odometer_reading.toLocaleString()} كم</span>}
                      {m.performed_by && <span>بواسطة: {m.performed_by}</span>}
                    </div>
                    {m.parts_replaced && <p className="record-parts">قطع مستبدلة: {m.parts_replaced}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Documents Tab */}
        {activeTab === 'documents' && (
          <div className="tab-section">
            <div className="section-header">
              <h3>وثائق المركبة</h3>
              <button className="btn btn--primary btn--sm" onClick={() => setShowDocumentModal(true)}>{I.plus} إضافة وثيقة</button>
            </div>
            {documents.length === 0 ? (
              <div className="empty-state">{I.file}<p>لا يوجد وثائق</p></div>
            ) : (
              <div className="documents-grid">
                {documents.map(d => (
                  <div key={d.id} className={`document-card ${d.is_expired ? 'expired' : d.days_to_expiry <= 30 ? 'expiring' : ''}`}>
                    <div className="document-icon">{I.file}</div>
                    <div className="document-info">
                      <h4>{documentTypes.find(t => t.value === d.document_type)?.label || d.document_type}</h4>
                      {d.document_number && <p>رقم: {d.document_number}</p>}
                      <p>تنتهي: {formatDate(d.expiry_date)}</p>
                      {d.is_expired && <span className="badge badge--danger">منتهية</span>}
                      {!d.is_expired && d.days_to_expiry <= 30 && <span className="badge badge--warning">تنتهي قريباً</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Fuel Tab */}
        {activeTab === 'fuel' && (
          <div className="tab-section">
            <div className="section-header">
              <div>
                <h3>سجل الوقود</h3>
                <p>{fuel.total_records} تعبئة • {fuel.total_liters.toLocaleString()} لتر • {formatCurrency(fuel.total_cost)}</p>
              </div>
              <button className="btn btn--primary btn--sm" onClick={() => setShowFuelModal(true)}>{I.plus} تسجيل تعبئة</button>
            </div>
            {fuel.items.length === 0 ? (
              <div className="empty-state">{I.fuel}<p>لا يوجد سجلات وقود</p></div>
            ) : (
              <div className="records-list">
                {fuel.items.map(f => (
                  <div key={f.id} className="record-card fuel-card">
                    <div className="record-header">
                      <span className="fuel-amount">{f.liters} لتر</span>
                      <span className="record-date">{formatDate(f.filled_at)}</span>
                    </div>
                    <div className="record-meta">
                      <span>التكلفة: {formatCurrency(f.cost)}{formatCurrency(f.cost) !== '-' ? <>{' '}<SAR className="h-[0.8em] w-auto" /></> : ''}</span>
                      <span>سعر اللتر: {f.cost_per_liter}{f.cost_per_liter ? <>{' '}<SAR className="h-[0.8em] w-auto" /></> : ''}</span>
                      {f.odometer_reading && <span>العداد: {f.odometer_reading.toLocaleString()} كم</span>}
                      {f.station && <span>المحطة: {f.station}</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Drivers Tab */}
        {activeTab === 'drivers' && (
          <div className="tab-section">
            <div className="section-header">
              <h3>السائقين المعينين</h3>
              <button className="btn btn--primary btn--sm" onClick={() => setShowDriverModal(true)}>{I.plus} تعيين سائق</button>
            </div>
            {drivers.current.length === 0 ? (
              <div className="empty-state">{I.user}<p>لا يوجد سائقين معينين حالياً</p></div>
            ) : (
              <div className="drivers-list">
                {drivers.current.map(d => (
                  <div key={d.id} className="driver-card">
                    <div className="driver-avatar">{d.driver_photo ? <img src={d.driver_photo} alt="" /> : I.user}</div>
                    <div className="driver-info">
                      <h4>{d.driver_name}</h4>
                      <p>{d.driver_phone}</p>
                      <span className="driver-dates">من {formatDate(d.start_date)} {d.end_date ? `إلى ${formatDate(d.end_date)}` : ''}</span>
                      {d.is_primary && <span className="badge badge--primary">رئيسي</span>}
                    </div>
                    <button className="btn btn--danger btn--sm" onClick={() => handleUnassignDriver(d.id)}>{I.trash}</button>
                  </div>
                ))}
              </div>
            )}
            {drivers.history.length > 0 && (
              <>
                <h4 style={{ marginTop: '2rem' }}>السائقين السابقين</h4>
                <div className="history-list">
                  {drivers.history.map(d => (
                    <div key={d.id} className="history-item">
                      <span>{d.driver_name}</span>
                      <span>{formatDate(d.start_date)} - {formatDate(d.end_date)}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* Incidents Tab */}
        {activeTab === 'incidents' && (
          <div className="tab-section">
            <div className="section-header">
              <h3>سجل الحوادث</h3>
              <button className="btn btn--primary btn--sm" onClick={() => setShowIncidentModal(true)}>{I.plus} تسجيل حادث</button>
            </div>
            {incidents.length === 0 ? (
              <div className="empty-state">{I.alert}<p>لا يوجد حوادث مسجلة</p></div>
            ) : (
              <div className="records-list">
                {incidents.map(inc => (
                  <div key={inc.id} className="record-card incident-card">
                    <div className="record-header">
                      <span className="incident-type">{incidentTypes.find(t => t.value === inc.incident_type)?.label || inc.incident_type}</span>
                      <span className={`badge badge--${inc.status === 'resolved' ? 'success' : inc.status === 'investigating' ? 'warning' : 'danger'}`}>
                        {inc.status === 'resolved' ? 'تم الحل' : inc.status === 'investigating' ? 'قيد التحقيق' : 'مبلغ عنه'}
                      </span>
                    </div>
                    <p className="record-desc">{inc.description}</p>
                    <div className="record-meta">
                      <span>التاريخ: {formatDate(inc.incident_date)}</span>
                      {inc.location && <span>الموقع: {inc.location}</span>}
                      {inc.driver_name && <span>السائق: {inc.driver_name}</span>}
                      {inc.damage_cost && <span>تكلفة الضرر: {formatCurrency(inc.damage_cost)}</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Stats Tab */}
        {activeTab === 'stats' && stats && (
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-icon" style={{ background: '#6366f1' }}>{I.wrench}</div>
              <div className="stat-info">
                <h4>الصيانة</h4>
                <p className="stat-value">{stats.maintenance.total_records}</p>
                <span>إجمالي التكلفة: {formatCurrency(stats.maintenance.total_cost)}</span>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon" style={{ background: '#10b981' }}>{I.fuel}</div>
              <div className="stat-info">
                <h4>الوقود</h4>
                <p className="stat-value">{stats.fuel.total_fills}</p>
                <span>{stats.fuel.total_liters.toLocaleString()} لتر • {formatCurrency(stats.fuel.total_cost)}</span>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon" style={{ background: '#f59e0b' }}>{I.alert}</div>
              <div className="stat-info">
                <h4>الحوادث</h4>
                <p className="stat-value">{stats.incidents.total}</p>
                <span>مفتوحة: {stats.incidents.open}</span>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon" style={{ background: '#8b5cf6' }}>{I.truck}</div>
              <div className="stat-info">
                <h4>الطلبات</h4>
                <p className="stat-value">{stats.orders.total}</p>
                <span>مكتملة: {stats.orders.completed}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Maintenance Modal */}
      {showMaintenanceModal && (
        <div className="modal-overlay" onClick={() => setShowMaintenanceModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>تسجيل صيانة</h2>
              <button className="modal-close" onClick={() => setShowMaintenanceModal(false)}>{I.x}</button>
            </div>
            <form onSubmit={handleAddMaintenance}>
              <div className="modal-body">
                <div className="form-grid">
                  <div className="form-group">
                    <label>نوع الصيانة *</label>
                    <select value={maintenanceForm.maintenance_type} onChange={e => setMaintenanceForm({...maintenanceForm, maintenance_type: e.target.value})} required>
                      <option value="">اختر</option>
                      {maintenanceTypes.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>التكلفة</label>
                    <input type="number" step="0.01" value={maintenanceForm.cost} onChange={e => setMaintenanceForm({...maintenanceForm, cost: e.target.value})} />
                  </div>
                  <div className="form-group">
                    <label>قراءة العداد</label>
                    <input type="number" value={maintenanceForm.odometer_reading} onChange={e => setMaintenanceForm({...maintenanceForm, odometer_reading: e.target.value})} />
                  </div>
                  <div className="form-group">
                    <label>تاريخ الصيانة</label>
                    <input type="date" value={maintenanceForm.performed_at} onChange={e => setMaintenanceForm({...maintenanceForm, performed_at: e.target.value})} />
                  </div>
                  <div className="form-group">
                    <label>بواسطة</label>
                    <input type="text" value={maintenanceForm.performed_by} onChange={e => setMaintenanceForm({...maintenanceForm, performed_by: e.target.value})} />
                  </div>
                  <div className="form-group">
                    <label>الصيانة القادمة</label>
                    <input type="date" value={maintenanceForm.next_maintenance_date} onChange={e => setMaintenanceForm({...maintenanceForm, next_maintenance_date: e.target.value})} />
                  </div>
                  <div className="form-group full-width">
                    <label>الوصف</label>
                    <textarea value={maintenanceForm.description} onChange={e => setMaintenanceForm({...maintenanceForm, description: e.target.value})} rows="2" />
                  </div>
                  <div className="form-group full-width">
                    <label>القطع المستبدلة</label>
                    <input type="text" value={maintenanceForm.parts_replaced} onChange={e => setMaintenanceForm({...maintenanceForm, parts_replaced: e.target.value})} />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn--secondary" onClick={() => setShowMaintenanceModal(false)}>إلغاء</button>
                <button type="submit" className="btn btn--primary">حفظ</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Document Modal */}
      {showDocumentModal && (
        <div className="modal-overlay" onClick={() => setShowDocumentModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>إضافة وثيقة</h2>
              <button className="modal-close" onClick={() => setShowDocumentModal(false)}>{I.x}</button>
            </div>
            <form onSubmit={handleAddDocument}>
              <div className="modal-body">
                <div className="form-grid">
                  <div className="form-group">
                    <label>نوع الوثيقة *</label>
                    <select value={documentForm.document_type} onChange={e => setDocumentForm({...documentForm, document_type: e.target.value})} required>
                      <option value="">اختر</option>
                      {documentTypes.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>رقم الوثيقة</label>
                    <input type="text" value={documentForm.document_number} onChange={e => setDocumentForm({...documentForm, document_number: e.target.value})} />
                  </div>
                  <div className="form-group">
                    <label>تاريخ الإصدار</label>
                    <input type="date" value={documentForm.issue_date} onChange={e => setDocumentForm({...documentForm, issue_date: e.target.value})} />
                  </div>
                  <div className="form-group">
                    <label>تاريخ الانتهاء</label>
                    <input type="date" value={documentForm.expiry_date} onChange={e => setDocumentForm({...documentForm, expiry_date: e.target.value})} />
                  </div>
                  <div className="form-group full-width">
                    <label>ملاحظات</label>
                    <textarea value={documentForm.notes} onChange={e => setDocumentForm({...documentForm, notes: e.target.value})} rows="2" />
                  </div>
                  <div className="form-group full-width">
                    <label>رفع ملف الوثيقة</label>
                    <input
                      type="file"
                      accept="image/*,.pdf"
                      onChange={e => setDocumentFile(e.target.files[0] || null)}
                      style={{padding: '8px', border: '1px dashed var(--tenant-border)', borderRadius: '8px', width: '100%'}}
                    />
                    {documentFile && <small style={{color: 'var(--tenant-primary)'}}>✓ {documentFile.name}</small>}
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn--secondary" onClick={() => setShowDocumentModal(false)}>إلغاء</button>
                <button type="submit" className="btn btn--primary">حفظ</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Fuel Modal */}
      {showFuelModal && (
        <div className="modal-overlay" onClick={() => setShowFuelModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>تسجيل تعبئة وقود</h2>
              <button className="modal-close" onClick={() => setShowFuelModal(false)}>{I.x}</button>
            </div>
            <form onSubmit={handleAddFuel}>
              <div className="modal-body">
                <div className="form-grid">
                  <div className="form-group">
                    <label>الكمية (لتر) *</label>
                    <input type="number" step="0.01" value={fuelForm.liters} onChange={e => setFuelForm({...fuelForm, liters: e.target.value})} required />
                  </div>
                  <div className="form-group">
                    <label>التكلفة *</label>
                    <input type="number" step="0.01" value={fuelForm.cost} onChange={e => setFuelForm({...fuelForm, cost: e.target.value})} required />
                  </div>
                  <div className="form-group">
                    <label>قراءة العداد</label>
                    <input type="number" value={fuelForm.odometer_reading} onChange={e => setFuelForm({...fuelForm, odometer_reading: e.target.value})} />
                  </div>
                  <div className="form-group">
                    <label>التاريخ</label>
                    <input type="date" value={fuelForm.filled_at} onChange={e => setFuelForm({...fuelForm, filled_at: e.target.value})} />
                  </div>
                  <div className="form-group">
                    <label>نوع الوقود</label>
                    <select value={fuelForm.fuel_type} onChange={e => setFuelForm({...fuelForm, fuel_type: e.target.value})}>
                      <option value="">اختر</option>
                      <option value="diesel">ديزل</option>
                      <option value="petrol">بنزين</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>المحطة</label>
                    <input type="text" value={fuelForm.station} onChange={e => setFuelForm({...fuelForm, station: e.target.value})} />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn--secondary" onClick={() => setShowFuelModal(false)}>إلغاء</button>
                <button type="submit" className="btn btn--primary">حفظ</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Driver Assignment Modal */}
      {showDriverModal && (
        <div className="modal-overlay" onClick={() => setShowDriverModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>تعيين سائق</h2>
              <button className="modal-close" onClick={() => setShowDriverModal(false)}>{I.x}</button>
            </div>
            <form onSubmit={handleAssignDriver}>
              <div className="modal-body">
                <div className="form-grid">
                  <div className="form-group full-width">
                    <label>السائق *</label>
                    <select value={driverForm.driver_id} onChange={e => setDriverForm({...driverForm, driver_id: e.target.value})} required>
                      <option value="">اختر سائق</option>
                      {availableDrivers.map(d => <option key={d.id} value={d.id}>{d.full_name} - {d.phone}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>تاريخ البدء</label>
                    <input type="date" value={driverForm.start_date} onChange={e => setDriverForm({...driverForm, start_date: e.target.value})} />
                  </div>
                  <div className="form-group">
                    <label className="checkbox-label">
                      <input type="checkbox" checked={driverForm.is_primary} onChange={e => setDriverForm({...driverForm, is_primary: e.target.checked})} />
                      سائق رئيسي
                    </label>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn--secondary" onClick={() => setShowDriverModal(false)}>إلغاء</button>
                <button type="submit" className="btn btn--primary">تعيين</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Incident Modal */}
      {showIncidentModal && (
        <div className="modal-overlay" onClick={() => setShowIncidentModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>تسجيل حادث</h2>
              <button className="modal-close" onClick={() => setShowIncidentModal(false)}>{I.x}</button>
            </div>
            <form onSubmit={handleAddIncident}>
              <div className="modal-body">
                <div className="form-grid">
                  <div className="form-group">
                    <label>نوع الحادث *</label>
                    <select value={incidentForm.incident_type} onChange={e => setIncidentForm({...incidentForm, incident_type: e.target.value})} required>
                      <option value="">اختر</option>
                      {incidentTypes.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>التاريخ</label>
                    <input type="date" value={incidentForm.incident_date} onChange={e => setIncidentForm({...incidentForm, incident_date: e.target.value})} />
                  </div>
                  <div className="form-group">
                    <label>الموقع</label>
                    <input type="text" value={incidentForm.location} onChange={e => setIncidentForm({...incidentForm, location: e.target.value})} />
                  </div>
                  <div className="form-group">
                    <label>تكلفة الضرر</label>
                    <input type="number" step="0.01" value={incidentForm.damage_cost} onChange={e => setIncidentForm({...incidentForm, damage_cost: e.target.value})} />
                  </div>
                  <div className="form-group full-width">
                    <label>الوصف *</label>
                    <textarea value={incidentForm.description} onChange={e => setIncidentForm({...incidentForm, description: e.target.value})} rows="3" required />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn--secondary" onClick={() => setShowIncidentModal(false)}>إلغاء</button>
                <button type="submit" className="btn btn--primary">حفظ</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        .vehicle-detail-page { padding: 1.5rem; }
        .detail-header { display: flex; align-items: center; gap: 1rem; margin-bottom: 1.5rem; flex-wrap: wrap; }
        .back-btn { background: var(--tenant-bg-secondary); border: none; padding: 0.5rem; border-radius: 8px; cursor: pointer; display: flex; }
        .back-btn svg { width: 20px; height: 20px; color: var(--tenant-text); }
        .detail-header__info { display: flex; align-items: center; gap: 1rem; flex: 1; }
        .detail-avatar { width: 60px; height: 60px; border-radius: 12px; display: flex; align-items: center; justify-content: center; }
        .detail-avatar svg { width: 30px; height: 30px; color: white; }
        .detail-header__text h1 { font-size: 1.5rem; margin: 0; color: var(--tenant-text); }
        .detail-header__text p { margin: 0.25rem 0 0; color: var(--tenant-text-secondary); font-family: monospace; }
        
        .detail-tabs { display: flex; gap: 0.5rem; overflow-x: auto; padding-bottom: 1rem; border-bottom: 1px solid var(--tenant-border); margin-bottom: 1.5rem; }
        .detail-tab { display: flex; align-items: center; gap: 0.5rem; padding: 0.75rem 1rem; border: none; background: transparent; color: var(--tenant-text-secondary); cursor: pointer; border-radius: 8px; white-space: nowrap; transition: all 0.2s; }
        .detail-tab svg { width: 18px; height: 18px; }
        .detail-tab:hover { background: var(--tenant-bg-secondary); }
        .detail-tab.active { background: var(--tenant-primary); color: white; }
        
        .info-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 1rem; }
        .info-card { background: var(--tenant-bg-secondary); border-radius: 12px; padding: 1.25rem; }
        .info-card h3 { margin: 0 0 1rem; font-size: 1rem; color: var(--tenant-text); }
        .info-row { display: flex; justify-content: space-between; padding: 0.5rem 0; border-bottom: 1px solid var(--tenant-border); }
        .info-row:last-child { border-bottom: none; }
        .info-row span { color: var(--tenant-text-secondary); }
        .info-row strong { color: var(--tenant-text); }
        .text-danger { color: var(--tenant-danger) !important; }
        
        .tab-section { background: var(--tenant-bg-secondary); border-radius: 12px; padding: 1.25rem; }
        .section-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 1rem; flex-wrap: wrap; gap: 1rem; }
        .section-header h3 { margin: 0; }
        .section-header p { margin: 0.25rem 0 0; color: var(--tenant-text-secondary); font-size: 0.875rem; }
        
        .empty-state { text-align: center; padding: 3rem; color: var(--tenant-text-secondary); }
        .empty-state svg { width: 48px; height: 48px; margin-bottom: 1rem; opacity: 0.5; }
        
        .records-list { display: flex; flex-direction: column; gap: 1rem; }
        .record-card { background: var(--tenant-bg); border-radius: 8px; padding: 1rem; border: 1px solid var(--tenant-border); }
        .record-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.5rem; }
        .record-type, .incident-type { font-weight: 600; color: var(--tenant-primary); }
        .fuel-amount { font-weight: 600; font-size: 1.125rem; color: var(--tenant-success); }
        .record-date { color: var(--tenant-text-secondary); font-size: 0.875rem; }
        .record-desc { margin: 0.5rem 0; color: var(--tenant-text); }
        .record-meta { display: flex; flex-wrap: wrap; gap: 1rem; font-size: 0.875rem; color: var(--tenant-text-secondary); }
        .record-parts { margin: 0.5rem 0 0; font-size: 0.875rem; color: var(--tenant-text-secondary); }
        
        .documents-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 1rem; }
        .document-card { background: var(--tenant-bg); border-radius: 8px; padding: 1rem; border: 1px solid var(--tenant-border); display: flex; gap: 1rem; }
        .document-card.expired { border-color: var(--tenant-danger); background: rgba(239, 68, 68, 0.05); }
        .document-card.expiring { border-color: var(--tenant-warning); background: rgba(245, 158, 11, 0.05); }
        .document-icon { width: 40px; height: 40px; background: var(--tenant-bg-secondary); border-radius: 8px; display: flex; align-items: center; justify-content: center; }
        .document-icon svg { width: 20px; height: 20px; color: var(--tenant-text-secondary); }
        .document-info h4 { margin: 0 0 0.25rem; font-size: 0.875rem; }
        .document-info p { margin: 0; font-size: 0.75rem; color: var(--tenant-text-secondary); }
        
        .drivers-list { display: flex; flex-direction: column; gap: 1rem; }
        .driver-card { background: var(--tenant-bg); border-radius: 8px; padding: 1rem; border: 1px solid var(--tenant-border); display: flex; align-items: center; gap: 1rem; }
        .driver-avatar { width: 48px; height: 48px; border-radius: 50%; background: var(--tenant-bg-secondary); display: flex; align-items: center; justify-content: center; overflow: hidden; }
        .driver-avatar img { width: 100%; height: 100%; object-fit: cover; }
        .driver-avatar svg { width: 24px; height: 24px; color: var(--tenant-text-secondary); }
        .driver-info { flex: 1; }
        .driver-info h4 { margin: 0; }
        .driver-info p { margin: 0.25rem 0; color: var(--tenant-text-secondary); font-size: 0.875rem; }
        .driver-dates { font-size: 0.75rem; color: var(--tenant-text-secondary); }
        
        .history-list { display: flex; flex-direction: column; gap: 0.5rem; }
        .history-item { display: flex; justify-content: space-between; padding: 0.75rem; background: var(--tenant-bg); border-radius: 6px; font-size: 0.875rem; }
        
        .stats-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 1rem; }
        .stat-card { background: var(--tenant-bg-secondary); border-radius: 12px; padding: 1.25rem; display: flex; gap: 1rem; }
        .stat-icon { width: 48px; height: 48px; border-radius: 12px; display: flex; align-items: center; justify-content: center; }
        .stat-icon svg { width: 24px; height: 24px; color: white; }
        .stat-info h4 { margin: 0; font-size: 0.875rem; color: var(--tenant-text-secondary); }
        .stat-value { font-size: 1.5rem; font-weight: 700; margin: 0.25rem 0; color: var(--tenant-text); }
        .stat-info span { font-size: 0.75rem; color: var(--tenant-text-secondary); }
        
        .form-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 1rem; }
        .form-group.full-width { grid-column: span 2; }
        .form-group label { display: block; margin-bottom: 0.5rem; font-weight: 500; color: var(--tenant-text); }
        .form-group input, .form-group select, .form-group textarea { width: 100%; padding: 0.75rem; border: 1px solid var(--tenant-border); border-radius: 8px; background: var(--tenant-bg); color: var(--tenant-text); }
        .checkbox-label { display: flex; align-items: center; gap: 0.5rem; cursor: pointer; }
        .checkbox-label input { width: auto; }
        
        @media (max-width: 600px) {
          .form-grid { grid-template-columns: 1fr; }
          .form-group.full-width { grid-column: span 1; }
          .detail-tabs { gap: 0.25rem; }
          .detail-tab span { display: none; }
        }
      `}</style>
    </div>
  );
};

export default VehicleDetailPage;
